import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import API_BASE_URL from '../config';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [adminToEdit, setAdminToEdit] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editRole, setEditRole] = useState('admin');

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return 'Invalid Date';
    }
  };

  const formatRole = (role) => {
    if (role === 'super_admin') {
      return 'Super Admin';
    }
    return 'Admin';
  };

  const handleAuthFailure = (message) => {
    setError(message);
    setIsAuthenticated(false);
    setAccessDenied(false);
    localStorage.removeItem('adminToken');
  };

  const fetchAdmins = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAuthenticated(false);
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/admin/admins`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const newToken = response.headers.get('X-New-Token');
      if (newToken) {
        localStorage.setItem('adminToken', newToken);
      }

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          if (response.status === 403) {
            setAccessDenied(true);
            setIsAuthenticated(true);
            setAdmins([]);
            return;
          }
          handleAuthFailure(data?.error || 'Authentication required');
          return;
        }
        throw new Error(data?.error || 'Failed to fetch admins');
      }

      if (!Array.isArray(data)) {
        console.error('Invalid data format:', data);
        throw new Error('Invalid data format received');
      }

      setAdmins(data);
    } catch (fetchError) {
      console.error('Fetch admins error:', fetchError);
      setError(fetchError.message);
      if (fetchError.message.includes('Authentication')) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const loginResponse = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      let data;
      try {
        const text = await loginResponse.text();
        data = text ? JSON.parse(text) : null;
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Invalid response from server');
      }

      if (!loginResponse.ok) {
        throw new Error(data?.error || 'Invalid credentials');
      }

      if (!data || !data.token) {
        throw new Error('Invalid response from server');
      }

      localStorage.setItem('adminToken', data.token);
      setIsAuthenticated(true);
      setEmail('');
      setPassword('');
      setAccessDenied(false);

      await fetchAdmins();
    } catch (loginError) {
      console.error('Login error:', loginError);
      setError(loginError.message);
      setIsAuthenticated(false);
      localStorage.removeItem('adminToken');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setIsAuthenticated(false);
        setAuthChecking(false);
        return;
      }

      try {
        setIsAuthenticated(true);
        await fetchAdmins();
      } catch (checkError) {
        console.error('Auth check failed:', checkError);
        setError(checkError.message);
        setIsAuthenticated(false);
        localStorage.removeItem('adminToken');
      } finally {
        setAuthChecking(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setEmail('');
    setPassword('');
    setAdmins([]);
    setError(null);
    setSuccessMessage('');
    setAccessDenied(false);
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage('');

    if (!newAdminEmail || !newAdminPassword) {
      setError('Email and password are required');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: newAdminEmail, password: newAdminPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleAuthFailure(data?.error || 'Authentication required');
          return;
        }
        throw new Error(data?.error || 'Failed to create admin');
      }

      setAdmins((prev) => [...prev, data.admin].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
      setSuccessMessage(data.message || 'Admin created successfully');
      setNewAdminEmail('');
      setNewAdminPassword('');
    } catch (createError) {
      setError(createError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = (admin) => {
    setAdminToEdit(admin);
    setEditEmail(admin.email || '');
    setEditPassword('');
    setEditRole(admin.role || 'admin');
    setEditDialogOpen(true);
  };

  const confirmEditAdmin = async (event) => {
    event.preventDefault();
    if (!adminToEdit) return;

    if (!editEmail && !editPassword && !editRole) {
      setError('Email, password, or role is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('adminToken');
      const body = {};
      if (editEmail) {
        body.email = editEmail;
      }
      if (editPassword) {
        body.password = editPassword;
      }
      if (editRole) {
        body.role = editRole;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/admins/${adminToEdit._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleAuthFailure(data?.error || 'Authentication required');
          return;
        }
        throw new Error(data?.error || 'Failed to update admin');
      }

      setAdmins((prev) => prev.map((admin) => (admin._id === adminToEdit._id ? data.admin : admin)));
      setSuccessMessage(data.message || 'Admin updated successfully');
      setEditDialogOpen(false);
      setAdminToEdit(null);
      setEditEmail('');
      setEditPassword('');
      setEditRole('admin');
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = (admin) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAdmin = async () => {
    if (!adminToDelete) return;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/admins/${adminToDelete._id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleAuthFailure(data?.error || 'Authentication required');
          return;
        }
        throw new Error(data?.error || 'Failed to remove admin');
      }

      setAdmins((prev) => prev.filter((admin) => admin._id !== adminToDelete._id));
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
      setSuccessMessage(data.message || 'Admin removed successfully');
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteAdmin = () => {
    setDeleteDialogOpen(false);
    setAdminToDelete(null);
  };

  if (authChecking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
          maxWidth: 400,
          mx: 'auto',
          mt: 4,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography variant="h5" gutterBottom>
          Super Admin Access
        </Typography>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          sx={{ mb: 2 }}
          autoComplete="off"
          inputProps={{
            autoComplete: 'off',
            form: {
              autoComplete: 'off'
            }
          }}
        />
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={!!error}
          helperText={error}
          sx={{ mb: 2 }}
          autoComplete="new-password"
          inputProps={{
            autoComplete: 'new-password',
            form: {
              autoComplete: 'off'
            }
          }}
        />
        <Button fullWidth variant="contained" type="submit" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Login'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Admin Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {accessDenied && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Super admin access required. Return to the admin panel for standard admin tools.
        </Alert>
      )}

      {accessDenied ? (
        <Button variant="outlined" component={RouterLink} to="/admin">
          Back to Admin Panel
        </Button>
      ) : (
      <Box
        component="form"
        onSubmit={handleCreateAdmin}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3
        }}
      >
        <TextField
          label="New Admin Email"
          type="email"
          value={newAdminEmail}
          onChange={(event) => setNewAdminEmail(event.target.value)}
          autoComplete="off"
          fullWidth
        />
        <TextField
          label="Temporary Password"
          type="password"
          value={newAdminPassword}
          onChange={(event) => setNewAdminPassword(event.target.value)}
          autoComplete="new-password"
          fullWidth
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{ minWidth: { xs: '100%', sm: 180 } }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Add Admin'}
        </Button>
      </Box>
      )}

      {!accessDenied && (
        <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No admins found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => (
                  <TableRow key={admin._id}>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{formatRole(admin.role)}</TableCell>
                    <TableCell>{admin.createdAt ? formatDate(admin.createdAt) : 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleEditAdmin(admin)}
                          disabled={loading}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAdmin(admin)}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteAdmin}
        aria-labelledby="admin-delete-dialog-title"
        aria-describedby="admin-delete-dialog-description"
      >
        <DialogTitle id="admin-delete-dialog-title">
          Confirm Admin Removal
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="admin-delete-dialog-description">
            Remove admin access for {adminToDelete?.email}? This admin will no longer be able to sign in.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteAdmin} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteAdmin} color="error" variant="contained" autoFocus>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>
          Edit Admin
        </DialogTitle>
        <form onSubmit={confirmEditAdmin} autoComplete="off">
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              type="email"
              value={editEmail}
              onChange={(event) => setEditEmail(event.target.value)}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                form: {
                  autoComplete: 'off'
                }
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Reset Password (optional)"
              type="password"
              value={editPassword}
              onChange={(event) => setEditPassword(event.target.value)}
              helperText="Leave blank to keep current password"
              autoComplete="new-password"
              inputProps={{
                autoComplete: 'new-password',
                form: {
                  autoComplete: 'off'
                }
              }}
            />
            <TextField
              select
              fullWidth
              margin="normal"
              label="Role"
              value={editRole}
              onChange={(event) => setEditRole(event.target.value)}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
